import type { BillingSummary, PaymentStatusResponse, User } from '../types';
import { hasGuestPrivileges } from '../utils/roles';

export type PaymentScreen =
  | 'loading'
  | 'not_configured'
  | 'no_tier'
  | 'no_price'
  | 'idle'
  | 'resume'
  | 'redirecting'
  | 'processing'
  | 'pending_slow'
  | 'success'
  | 'canceled'
  | 'failed'
  | 'native_blocked';

// The server-derived requires_payment flag is authoritative on every platform.
// Native clients are blocked too, but see a neutral "membership not active"
// screen (no checkout, no pricing, no external purchase links) to stay within
// Apple/Google store rules.
// A member whose dues lapsed still owes, but keeps limited access instead of
// the paywall; they restart from a banner.
export function shouldEnforcePaymentGate(user: User | null): boolean {
  return Boolean(user?.requires_payment) && !user?.payment_lapsed;
}

export type SessionGate = 'onboarding' | 'payment' | 'app';

/** Where a signed-in person goes. Onboarding is finished before the paywall. */
export function sessionGate(
  user: Pick<User, 'has_seen_onboarding' | 'requires_payment' | 'payment_lapsed'> | null,
): SessionGate {
  if (user && !user.has_seen_onboarding) return 'onboarding';
  if (shouldEnforcePaymentGate(user as User)) return 'payment';
  return 'app';
}

/** Last step of registration. Web checkout opens only when the server can charge. */
export function routeAfterOnboarding(options: {
  isGuest: boolean;
  isNative: boolean;
  canCheckout: boolean;
}): '/payment' | '/' {
  if (!options.isGuest && shouldExposeWebCheckout(options.isNative) && options.canCheckout) {
    return '/payment';
  }
  return '/';
}

export function shouldExposeWebCheckout(isNative: boolean): boolean {
  return !isNative;
}

/** Settings → Membership & Billing self-serve (Checkout / Portal / card). */
export function canSelfServeBilling(
  user: User | null,
  billing?: Pick<BillingSummary, 'can_manage_billing'> | null,
): boolean {
  if (billing?.can_manage_billing === false) return false;
  if (user?.payment_lapsed && !user.limit_app_access && user.role !== 'guest') return true;
  if (hasGuestPrivileges(user)) return false;
  return true;
}

export function resolvePaymentScreen(
  data: PaymentStatusResponse,
): PaymentScreen {
  if (!data.requires_payment) return 'success';
  if (!data.configured) return 'not_configured';
  if (!data.can_checkout) return 'no_price';
  if (
    data.checkout_stale
    || data.payment_status === 'past_due'
    || data.payment_status === 'failed'
    || data.payment_status === 'canceled'
  ) {
    return 'failed';
  }
  // A pending status on a fresh page load means the member started Checkout
  // but never completed it (abandoned tab). Nothing is being "confirmed", so
  // offer an honest resume screen instead of a processing spinner. The
  // spinner is reserved for an actual return from Stripe's success redirect.
  if (data.payment_status === 'pending') return 'resume';
  return 'idle';
}
